import React, { useEffect, useState } from 'react';

import { BaseControlProps } from '../../models/BaseControlProps';
import { DataTable, Pager, Span } from '@tag/tag-components-react-v4';
import { useAuth } from '../../services/AuthContext';
import { UtilService } from '../../services/UtilService';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';
import { Box, Button, Card, CardContent, CircularProgress, Divider, Grid, Skeleton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import { UserLoginActivityRequest } from '../../models/UserLoginActivityRequest';
import { LoginActivities } from '../../models/LoginActivities';
import { UserAPIService } from '../../services/UserAPIService';
import { useSearchParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from "dayjs";

interface HeaderMap {
    userId: string;
    peopleXDId: string;
    userName: string;
    ipAddress: string;
    loginSuccessful: string;
    createdDate?: string;
}

const LoginActivitiesView = (props: BaseControlProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pageSize = 15;
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [loading, isLoading] = useState<boolean>(true);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [loginActivities, setLoginActivities] = useState<LoginActivities[]>([]);
    const authContext = useAuth();
    const { showMessage } = useMessage();
    const apiService = new UserAPIService();
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [ipAddressStr, setIPAddressStr] = useState<string>('');
    const [usernameStr, setUsernameStr] = useState<string>('');
    const [meppnoStr, setMeppnoStr] = useState<string>('');
    const [dateRange, setDateRange] = useState<{
            start: Dayjs | null;
            end: Dayjs | null;
        }>({ start: null, end: null});
    const [ urlParams ] = useSearchParams();
    const [scopeGet, applyGet] = useState<UserLoginActivityRequest>({
        pageSize: pageSize,
        pageNo: 0,
        filterSearchString: "",
        orderByString: "",
    });

    const getLoginActivities = () => {
        isLoading(true);
        scopeGet.filterSearchString = getSeachFilters();
        scopeGet.orderByString = getOrderByFilters();
        scopeGet.pageSize = pageSize;
        applyGet(scopeGet);
        apiService.getLoginActivities(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                if(props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                setLoginActivities(response.response?.activities ?? []);
                const pageCount =
                    response.response && response.response?.activities.length > 0
                        ? Math.ceil(response.response.totalCount / pageSize)
                        : 0;
                setPageCount(pageCount && pageCount > 0 ? pageCount : 0);
            }
            isLoading(false);
        })
    }

    const getLoginActivitiesForDownload = () => {
        setDownloading(true);
        scopeGet.filterSearchString = getSeachFilterForReport();
        scopeGet.orderByString = getOrderByFilters();
        scopeGet.pageSize = 0;
        applyGet(scopeGet);
        apiService.getLoginActivities(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                if(props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                const csvData = convertToCSV(response.response && response.response.activities ? response.response?.activities : []);
                const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                const formattedDateTime = moment(new Date()).format("DD-MM-YYYY_HH-mm");

                link.href = url;
                link.setAttribute('download', `login_data_${formattedDateTime}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            setDownloading(false);
        })
    }

    const getOrderByFilters = () => {
        return  `{
            "OrderByFields": [
              {
                "FieldName": "createdDate",
                "OrderDirection": "DESC"
              }
            ]
        }`
    }

    const getSeachFilters = () => {
        let searchFilters = [];

        if (usernameStr.trim() !== "") {
            searchFilters.push(`{"FieldName":"userName","SearchType":"startswith","SearchValue":"${usernameStr}"}`);
        }

        if (meppnoStr.trim() !== "") {
            searchFilters.push(`{"FieldName":"peopleXDId","SearchType":"contains","SearchValue":"${meppnoStr}"}`);
        }

        if (ipAddressStr.trim() !== "") {
            searchFilters.push(`{"FieldName":"ipAddress","SearchType":"startswith","SearchValue":"${ipAddressStr}"}`);
        }

        if (dateRange.start !== null && dateRange.end != null) {
            const dateStart = moment(dateRange.start.toISOString())
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            const dateEnd = moment(dateRange.end.toISOString())
                .endOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            searchFilters.push(`{"FieldName":"createdDate","SearchType":"greaterthanequal","SearchValue":"${dateStart}"}`);
            searchFilters.push(`{"FieldName":"createdDate","SearchType":"lessthanequal","SearchValue":"${dateEnd}"}`);
        }

        return searchFilters.length >= 0 ? `{"SearchFilters": [${searchFilters.join(",")}]}` : '';
    }

    const getSeachFilterForReport = () => {
        let searchFilters = [];

        if (dateRange.start == null || dateRange.end == null) {
            const reportDate = moment()
                .subtract(30, 'days')
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
    
            searchFilters.push(`{"FieldName":"CreatedDate","SearchType":"greaterthanequal","SearchValue":"${reportDate}"}`);
        }
        else {
            const dateStart = moment(dateRange.start.toISOString())
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            const dateEnd = moment(dateRange.end.toISOString())
                .endOf('day')
                .format('YYYY-MM-DD HH:mm:ss.SSS');
            searchFilters.push(`{"FieldName":"createdDate","SearchType":"greaterthanequal","SearchValue":"${dateStart}"}`);
            searchFilters.push(`{"FieldName":"createdDate","SearchType":"lessthanequal","SearchValue":"${dateEnd}"}`);
        }

        return searchFilters.length >= 0 ? `{"SearchFilters": [${searchFilters.join(",")}]}` : '';
    }

    const convertToCSV = (objArray: LoginActivities[] ) => {
        const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
        let csv = '';

        const headerMap: HeaderMap = {
            userId: 'User ID',
            peopleXDId: 'MEPP No.',
            userName: 'Name',
            ipAddress: 'IP Address',
            loginSuccessful: 'Login Success',
            createdDate: 'Created At'
        }

        // Type-safe function to get header name
        const getHeaderName = (key: string): string => {
            return headerMap[key as keyof HeaderMap] || key;
        };

        // Get headers in the order defined by headerMap
        const headers = Object.entries(headerMap)
            .map(([_, displayName]) => displayName);

        // Add headers to CSV
        csv += headers.join(',') + '\n';

        // Add data rows
        array.forEach((item: any) => {
             const row = Object.keys(headerMap)
            .map(key => {
                const value = item[key];
                
                // Format date fields
                if (key === 'createdDate' && value) {
                    return moment(value).format('DD-MM-YY HH:mm:ss');
                }
                // Handle boolean values
                if (typeof value === 'boolean') {
                    return value ? 'true' : 'false';
                }
                // Escape commas in strings
                if (typeof value === 'string' && value.includes(',')) {
                    return `"${value}"`;
                }
                return value;
            });

            csv += row.join(',') + '\n';
        });

        return csv;
    };

    const resetFilters = () => {
        setIPAddressStr('');
        setUsernameStr('');
        setMeppnoStr('');
        setDateRange({ start: null, end: null});
    }

    useEffect(() => {
        const userNameParam = urlParams.get('emailaddress');
        if (userNameParam && userNameParam !== null && userNameParam.length > 0) { setUsernameStr(userNameParam); }
        const getLoginActivitiesAsync = async () => {
            await getLoginActivities();
        }
        getLoginActivitiesAsync();
    }, []);

    useEffect(() => {
        if (dateRange.start !== null && dateRange.end == null ) {
            return;
        }
        
        if (dateRange.end !== null && dateRange.start == null) {
            return;
        }

        // Reset scope
        scopeGet.pageNo = 0;
        scopeGet.pageSize = pageSize;
        applyGet(scopeGet);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeoutId = setTimeout(() => {
            const getLoginActivitiesAsync = async () => {
                await getLoginActivities();
            }

            getLoginActivitiesAsync();
        }, 900);

        setSearchTimeout(timeoutId);

        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        }

    }, [ipAddressStr, usernameStr, meppnoStr, dateRange]);

    const columns = React.useMemo(
        () => [
            { 
                accessorKey: 'userId', 
                header: 'User ID'
            },
            {
                accessorKey: 'peopleXDId',
                header: 'MEPP No.'
            },
            { 
                accessorKey: 'userName', 
                header: 'Name'
            },
            { 
                accessorKey: 'ipAddress', 
                header: 'IP Address'
            },
            { 
                accessorKey: 'loginSuccessful', 
                header: 'Login Success',
                meta: { dataType: 'boolean' } 
            },
            { 
                accessorKey: 'createdDate', 
                header: 'Created At', 
                meta: { dataType: 'date' },
                cell: ({ row }: any) => {
                    const date = row.original.createdDate;
                    return date == '1900-01-01T00:00:00'
                        ? '-'
                        : moment(date).format('DD-MM-YY HH:mm:ss');
                }, 
            },
        ],
        [],
    );
    
    const handlePageChange = async (e: any) => {
        setPage(e.page - 1);
        scopeGet.pageNo = e.page - 1;
        
        await getLoginActivities();
    };

    const renderheaderView = () => {
        return (
            <Box sx={{ width: '100%', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        Login Activities
                    </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center" >
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="IP Address"
                            variant='outlined'
                            value={ipAddressStr}
                            onChange={(e) => setIPAddressStr(e.target.value )}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Username"
                            variant='outlined'
                            value={usernameStr}
                            onChange={(e) => setUsernameStr(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="MEPP No."
                            variant='outlined'
                            value={meppnoStr}
                            onChange={(e) => setMeppnoStr(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box display="flex" gap={2}>
                            <DatePicker
                                label="Start Date"
                                maxDate={dateRange.end || dayjs()} // Prevent selecting start after end
                                value={dateRange.start}
                                onChange={(date) => setDateRange({ ...dateRange, start: date })}
                            />
                            <DatePicker
                                label="End Date"
                                minDate={dateRange.start || undefined} // Prevent selecting end before start
                                maxDate={dayjs()} // Disable future dates
                                value={dateRange.end}
                                onChange={(date) => setDateRange({ ...dateRange, end: date })}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size='large'
                            style={{ minWidth: '80px' }}
                            onClick={() =>  {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                getLoginActivities();
                            }}
                        >
                            Filter
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            style={{ minWidth: '80px' }}
                            onClick={() => {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                resetFilters();
                            }}
                        >
                            Clear
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            disabled={downloading}
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            size='large'
                            style={{ minWidth: '80px' }}
                            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{
                                '&.Mui-disabled': {
                                    color: (theme) => theme.palette.primary.contrastText,
                                }
                            }}
                            onClick={() =>  {
                                getLoginActivitiesForDownload();
                            }}
                        >
                             {downloading ? 'Downloading...' : 'Download'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    const EmptyState = () => (
        <Box 
            sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.background.paper,
            }}
        >
            <Typography variant="body1" color="text.secondary">
            No payslips found
            </Typography>
        </Box>
    );

    // Loading skeleton for mobile
    const MobileLoadingSkeleton = () => (
        <Box sx={{ mb: 2 }}>
        {[...Array(3)].map((_, index) => (
            <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Box display="flex" justifyContent="flex-end">
                <Skeleton variant="rectangular" width={100} height={36} />
                </Box>
            </CardContent>
            </Card>
        ))}
        </Box>
    );

    if (isMobile) {
        return (
            <>
                {renderheaderView()}  

                <div>
                    {loading ? (
                    <MobileLoadingSkeleton />
                        ) : loginActivities.length === 0 ? (
                        <EmptyState />
                        ) : (
                        loginActivities.map((activity) => (
                            <Card key={activity.createdDate} sx={{ mb: 1 }}>
                                <CardContent>
                                    <Typography variant="subtitle2">
                                    User ID: {activity.userId}
                                    </Typography>
                                    <Typography variant="subtitle2">
                                    MEPP No.: {activity.peopleXDId}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                    Created: {activity.createdDate}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                    Name: {activity.userName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    IP Address: {activity.ipAddress}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Success: {activity.loginSuccessful.toString().toUpperCase()}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))
                    )}
                    {!loading && loginActivities.length > 0 && (
                        <Pager
                            accent='blue'
                            page={page + 1}
                            pageCount={pageCount}
                            onPageRequest={(e) => { handlePageChange(e); }}
                            style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                        />
                    )}
                </div>

            </>
        );
    }

    return(
        <>
            {renderheaderView()}

            <div style={{ background: '#fff' }}>
                <div style={{ height: 'auto' }}>
                    <DataTable 
                        isLoading={loading} 
                        loadingElement={<Span accent="blue">Loading ...</Span>} 
                        accent='blue' 
                        columns={columns} 
                        data={loginActivities} 
                    />
                </div>
                <Pager
                    accent='blue'
                    page={page + 1}
                    pageCount={pageCount}
                    onPageRequest={(e) => { handlePageChange(e); }}
                    style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                />
            </div>
        </>
    )
}
export default LoginActivitiesView;